import React, { FC, useCallback, useMemo, useRef } from 'react'
import { useLazyQuery } from '@apollo/client'
import { AsyncSelect } from '@wmgtech/legato'
import { useController, useFormContext } from 'react-hook-form'
import debounce from 'lodash/debounce'

import { formatDate } from 'shared/utils/formatDate'
import { SEARCH_GCDM_PRODUCTS } from 'modulesCommon/graphql/queries'
import { IArtistProduct, IProductForm, ISearchIGCDMProductResult, ISearchIGCDMProductArguments } from '../types'

import styles from './title-field.module.scss'

import { IFieldOptionProps, IFieldProps } from './field.props'

function optionAdapter(product: IArtistProduct, idx: number) {
  return {
    id: `${idx}`,
    name: product.displayTitle,
    artistTitle: product.artistDisplayTitle,
    gpid_list: product.gpid_list,
    releaseDate: product.releaseDate,
    label: `${product.displayTitle} (artist: ${product.artistDisplayTitle}`
  }
}

export const ProductTitleField: FC<IFieldProps> = ({ idx, disabled }) => {
  const [getGCDMProducts, { data, loading }] = useLazyQuery<ISearchIGCDMProductResult, ISearchIGCDMProductArguments>(
    SEARCH_GCDM_PRODUCTS,
    { fetchPolicy:'cache-and-network', notifyOnNetworkStatusChange: true }
  )

  const { control, getValues, setValue, trigger } = useFormContext<IProductForm>()
  const { field, fieldState: { error } } = useController({ name: `products.${idx}.name`, control })

  const prefix = useMemo<`products.${number}`>(() => `products.${idx}`, [idx])

  const gcdmProductsDictionary = useMemo<Record<string, IArtistProduct>>(
    () =>
      (data?.searchGcdmProducts.items ?? []).reduce<Record<string, IArtistProduct>>(
        (a, v, idx) => ({ ...a, [idx]: v }),
        {}
      ),
    [data]
  )

  const defaultValue = useMemo(() => {
    const values = getValues(prefix)
    const artistName = getValues('artistName')

    return values?.name ? [{
      id: values.id,
      name: values.name,
      artistTitle: artistName,
      gpid_list: values.gpid_list,
      releaseDate: values?.publish_date ? values.publish_date : ''
    }] : []
  }, [prefix, getValues])

  const dvOptions = useMemo(() => {
    const values = Object.values(gcdmProductsDictionary)

    return values.length ? values.map(optionAdapter) : defaultValue
  },[gcdmProductsDictionary, defaultValue])

  const onSelectProduct = useCallback<(item: IFieldOptionProps) => void>(
    (item) => {
      if (item) {
        setValue(prefix, {
          ...getValues(prefix),
          name: item.name,
          publish_date: item.releaseDate ? new Date(item.releaseDate).toISOString() : '',
          gpid_list: item.gpid_list
        })
        trigger(`${prefix}.name`)
      }
    },
  [getValues, prefix, setValue, trigger]
  )

  const onSearchHandler = useCallback(
    (term) => {
      const artistName = getValues('artistName')
      const opusId = getValues('opusId')
      const configuration = getValues(`${prefix}.configuration`)
      setValue(`${prefix}.name`, term)
      setValue(`${prefix}.gpid_list`, undefined)
      getGCDMProducts({
        variables: {
          input: {
            productName: term,
            configuration,
            artistName,
            ...(opusId && { artistOpusId: opusId }),
            limit: 20
          }
        }
      })
    },
    [getGCDMProducts, getValues, setValue, prefix]
  )

  const debounceSearch = useRef<(term: string) => void>(debounce(onSearchHandler, 500))

  return (
    <AsyncSelect
      {...field}
      disabled={disabled}
      list={loading ? [] : dvOptions}
      getTitle={({ name }) => name}
      getKey={({ name }) => name}
      onSelect={onSelectProduct}
      onSearch={debounceSearch.current}
      placeholder="Product Name"
      isLoading={loading}
      validation={{
        message: error?.message,
        ...(error && { type: 'error' })
      }}
      getElement={({ name, artistTitle, releaseDate }) => {
        const date = formatDate({ date: releaseDate, withTime: false })

        return (
          <div className={styles.item}>
            {`${name} (artist: ${artistTitle} ${releaseDate ? `, release date: ${date}` : ''})`}
          </div>
        )
      }}
    />
  )
}
